import type { Json } from './database.types'

/**
 * `approvals.context` is jsonb written by another system. Every read of it goes
 * through these, so a missing key, a renamed block or a shape the fleet changed
 * on a Tuesday degrades to an omitted section rather than a blank screen.
 *
 * Nothing here coerces. A number where a string belongs reads as absent, which
 * is the honest answer for a dashboard whose job is to not make things up.
 */

export function obj(value: Json | undefined | null): Record<string, Json | undefined> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, Json | undefined>)
    : null
}

export function arr(value: Json | undefined | null): Json[] {
  return Array.isArray(value) ? value : []
}

export function str(value: Json | undefined | null): string | null {
  return typeof value === 'string' && value.trim() ? value : null
}

export function num(value: Json | undefined | null): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

export function bool(value: Json | undefined | null): boolean | null {
  return typeof value === 'boolean' ? value : null
}

/** obj(a)?.b?.c without five lines of guards at every call site. */
export function at(root: Json | undefined | null, ...path: string[]): Json | undefined {
  let cursor: Json | undefined | null = root
  for (const key of path) {
    const next = obj(cursor)
    if (!next) return undefined
    cursor = next[key]
  }
  return cursor ?? undefined
}
